import { CSSProperties, ReactElement } from 'react';

import { appColors } from '@backrs_ui/common-ui/src/assets/appColors';
import { appStrings } from '@backrs_ui/common-ui/src/assets/appStrings';
import { BackrsIcon } from '@backrs_ui/common-ui/src/atoms/BackrsIcon';

import { Theme, SxProps, Typography, Box } from '@mui/material';

type NuggetPostProps = Readonly<{
  variant:
    | {
        type: 'SMALL';
        additionalQuote?: string;
        additionalQuoteAuthor?: string;
        contentSize?: 'SMALL' | 'LARGE';
      }
    | {
        type: 'LARGE';
        text: string;
        postUrl?: string;
        headshotImg?: string;
        userAbbreviatedName?: string;
        linkToBackrsProfile?: string;
        postDate?: string;
      };
}>;

export const NuggetPost = ({ variant }: NuggetPostProps): ReactElement => {
  if (variant.type === 'LARGE') {
    const getHowManyDaysAgo = (date: string): string => {
      const date1 = new Date(date);
      const date2 = new Date();
      const diffTime = Math.abs(date2.getTime() - date1.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      return `${diffDays} ${appStrings.modules.nugget.textResponseDaysAgo}`;
    };

    return (
      <Box
        onClick={variant.postUrl ? () => window.open(variant.postUrl, '_blank') : undefined}
        mt={4}
      >
        <Box
          position="relative"
          sx={{
            cursor: variant.postUrl ? 'pointer' : 'default',
          }}
        >
          <Box sx={largeQuoteIconContainerStyle}>
            <BackrsIcon name="IconQuotationLarge" alt="Quotation" />
          </Box>
          <Typography variant="body1" sx={bottomTextStyle}>
            {variant.text}
          </Typography>
        </Box>

        {/* we'll only display the headshot if we have both the headshot and the userAbbreviatedName */}
        {variant.userAbbreviatedName && (
          <Box
            sx={{
              ...imageHeadshotContainerStyle,
              cursor: variant.linkToBackrsProfile ? 'pointer' : 'default',
            }}
            onClick={
              variant.linkToBackrsProfile
                ? () => window.open(variant.linkToBackrsProfile, '_blank')
                : undefined
            }
          >
            <Box sx={imageContainerStyle}>
              {variant.headshotImg ? (
                <img
                  src={variant.headshotImg}
                  alt={`${variant.userAbbreviatedName} headshot`}
                  style={imageStyle}
                />
              ) : (
                <BackrsIcon name="IconHeadshot" alt="User" style={imageStyle} />
              )}
            </Box>
            <Box>
              <Typography variant="h6" mb={0}>
                {variant.userAbbreviatedName}
              </Typography>
              {variant.postDate && (
                <Typography variant="body1" sx={postDateTextStyle}>
                  {getHowManyDaysAgo(variant.postDate)}
                </Typography>
              )}
            </Box>
          </Box>
        )}
      </Box>
    );
  }

  return variant.additionalQuote ? (
    <Box sx={smallNuggetQuoteContainerStyle}>
      <Box sx={additionalQuoteContainerStyle}>
        <BackrsIcon
          name="IconQuotationLarge"
          alt="Quotation"
          height={variant.contentSize === 'SMALL' ? 16 : 20}
          width={variant.contentSize === 'SMALL' ? 16 : 20}
        />
        <Typography sx={additonalQuoteTextStyle}>{variant.additionalQuote}</Typography>
      </Box>
      {variant.additionalQuoteAuthor && (
        <Typography variant="body1" alignSelf={'flex-end'} mt={2} fontSize={'10pt'}>
          - {variant.additionalQuoteAuthor}
        </Typography>
      )}
    </Box>
  ) : (
    <></>
  );
};

const largeQuoteIconContainerStyle: SxProps<Theme> = {
  position: 'absolute',
  right: 32,
  top: -28,
};

const imageHeadshotContainerStyle: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'flex-start',
  flexDirection: 'row',
  gap: 2,
  mt: 4,
};

const additonalQuoteTextStyle: SxProps<Theme> = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  maxWidth: '100%',
  width: '40ch',
  fontStyle: 'italic',
  fontSize: '10.5pt',
  lineHeight: 1.4,
};

const postDateTextStyle: SxProps<Theme> = {
  color: appColors.neutral.gray1,
  fontSize: 12,
  marginTop: 0,
};

const additionalQuoteContainerStyle: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'row',
  gap: 1,
  justifyContent: 'flex-start',
};

const bottomTextStyle: SxProps<Theme> = {
  fontFamily: 'Sarabun',
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: '20px',
  maxWidth: '100%',
  whiteSpace: 'pre-wrap',
  padding: 3,
  borderRadius: 4,
  backgroundColor: appColors.color.blue4,
};

const imageContainerStyle: SxProps<Theme> = {
  borderRadius: '50%',
  width: 72,
  height: 72,
  overflow: 'hidden',
};

const imageStyle: CSSProperties = {
  width: '100%',
  height: '100%',
  objectFit: 'cover',
};

const smallNuggetQuoteContainerStyle: SxProps<Theme> = {
  mt: 3,
  borderLeft: `4px solid ${appColors.color.blue1}`,
  backgroundColor: appColors.color.blue4,
  padding: 2,
  flexDirection: 'column',
  display: 'flex',
  flexWrap: 'wrap',
  overflow: 'hidden',
};
